// When the police siren sounds (FE-24): a wahala starting (park-wide) or an arrest beginning near
// this keke. Read from what the server already sent: the `sabotage.started` event or the view's
// wahala turning on, and the `arrest.started` event or a new entry in the view's arrests. Both
// arrest fields are read loosely, so this works before and after the server sends them.
type Pos = { x: number; y: number };
export interface Siren {
  /** World units from this keke, or null for a park-wide wahala. */
  dist: number | null;
  /** -1 (left) to 1 (right). */
  pan: number;
}
interface ViewLike {
  you: { pos: Pos };
  sabotage?: unknown;
}

const isPos = (p: unknown): p is Pos => !!p && typeof (p as Pos).x === 'number' && typeof (p as Pos).y === 'number';

export class SirenWatch {
  private wahala: boolean | null = null;
  private arrests = 0;

  /** The sirens one frame calls for (the sound itself plays one at a time). */
  frame(view: ViewLike | null, events: ReadonlyArray<{ t: string }>): Siren[] {
    const out: Siren[] = [];
    const me = view?.you.pos;
    const near = (p: Pos): Siren | null => {
      if (!me) return null;
      return { dist: Math.hypot(p.x - me.x, p.y - me.y), pan: Math.max(-1, Math.min(1, (p.x - me.x) / 800)) };
    };
    for (const e of events) {
      if (e.t === 'sabotage.started') out.push({ dist: null, pan: 0 });
      const pos = (e as { pos?: unknown }).pos;
      if (e.t === 'arrest.started' && isPos(pos)) {
        const s = near(pos);
        if (s) out.push(s);
      }
    }
    if (!view) return out;
    const wahala = !!view.sabotage;
    // a round joined mid-wahala is old news: only a change from off to on counts
    if (wahala && this.wahala === false && !out.some((s) => s.dist === null)) out.push({ dist: null, pan: 0 });
    this.wahala = wahala;
    const list = (view as { arrests?: unknown }).arrests;
    const arrests = Array.isArray(list) ? list.filter((a) => isPos((a as { pos?: unknown })?.pos)) : [];
    if (arrests.length > this.arrests) {
      let best: Siren | null = null;
      for (const a of arrests) {
        const s = near((a as { pos: Pos }).pos);
        if (s && (!best || (s.dist ?? 0) < (best.dist ?? 0))) best = s;
      }
      if (best && !out.some((s) => s.dist !== null)) out.push(best);
    }
    this.arrests = arrests.length;
    return out;
  }
}
